import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
]

interface LanguagePreferenceModalProps {
  userId: string
  onSaved: () => void
  refreshProfile: () => Promise<void>
}

export default function LanguagePreferenceModal({
  userId,
  onSaved,
  refreshProfile,
}: LanguagePreferenceModalProps) {
  const { t, i18n } = useTranslation('common')
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setError(null)
    setSaving(true)
    try {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ preferred_language: selectedLanguage })
        .eq('id', userId)

      if (updateError) {
        setError(updateError.message)
        return
      }

      await refreshProfile()
      i18n.changeLanguage(selectedLanguage)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="language-preference-title"
    >
      <div
        className="max-w-md w-full bg-app-box rounded-2xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="language-preference-title" className="text-xl font-semibold text-gray-900 mb-2">
          {t('languagePreference.title')}
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          {t('languagePreference.description')}
        </p>

        <div className="space-y-2 mb-6">
          {languages.map((language) => (
            <button
              key={language.code}
              type="button"
              onClick={() => setSelectedLanguage(language.code)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                selectedLanguage === language.code
                  ? 'bg-orange-50 text-orange-600 border-2 border-orange-500'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <span className="text-lg">{language.flag}</span>
              <span>{language.name}</span>
              {selectedLanguage === language.code && (
                <svg
                  className="w-4 h-4 ml-auto text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>

        {error && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors"
        >
          {saving ? t('languagePreference.saving') : t('buttons.save')}
        </button>
      </div>
    </div>
  )
}
